import { Component, EventEmitter, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take, takeUntil } from 'rxjs/operators';
import { AnessaTranslationService } from '../../../../helper/translation/anessa-translation.service';
import { DeactivationDialogService } from '../../../../helper/can-deactivate/deactivation-dialog.service';
import { ProductFeeType, Product, ProductType } from '../../../../models/project/plant-specifications/product.model';
import { UserpilotReloader } from '../../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-edit-generated-product-financial-dialog',
  templateUrl: './edit-generated-product-financial-dialog.component.html',
  styleUrls: [
    './edit-generated-product-financial-dialog.component.scss',
  ]
})
export class EditGeneratedProductFinancialDialogComponent extends UserpilotReloader implements OnInit, OnDestroy {
  onDestroyNotifier = new EventEmitter();
  /** trigger for translation pipe */
  trigger: string;

  @ViewChild('genProdForm') genProdForm: NgForm;

  product: Product;

  priceTypes = [
    { name: 'FEE_TYPE.income', value: ProductFeeType.INCOME },
    { name: 'FEE_TYPE.cost', value: ProductFeeType.COST },
    { name: 'FEE_TYPE.free', value: ProductFeeType.FREE },
  ];
  FinancialPriceType = ProductFeeType;

  digestateTypes = [
    ProductType.SolidDigestate,
    ProductType.ThinDigestate,
    ProductType.WholeDigestate,
  ];

  showPriceType = false;

  constructor(
    private dialogRef: MatDialogRef<EditGeneratedProductFinancialDialogComponent>,
    private dialogService: DeactivationDialogService,
    public translationService: AnessaTranslationService,
    @Inject(MAT_DIALOG_DATA) private data: { product: Product },
  ) {
    super();
    if (this.data) {
      this.product = this.data.product;
      if (this.product && this.product.isSellableDigestate()) {
        this.showPriceType = true;
      }
    }
  }

  ngOnInit() {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    this.dialogRef.backdropClick().subscribe(() => {
      if (this.genProdForm && this.genProdForm.dirty) {
        this.dialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'))
          .pipe(take(1)).subscribe((result: boolean) => {
            if (result) {
              this.dialogRef.close();
            }
          });
      } else {
        this.dialogRef.close();
      }
    });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * Get the units types for the current product.
   */
  getUnitString() {
    switch (this.product.type) {
      case ProductType.Electric:
        return 'Energy';
      case ProductType.Heat:
        return 'Thermal_Quantity';
      case ProductType.Biometh:
        return 'Volume_Gas';
      case ProductType.SolidDigestate:
        return 'Mass';
      case ProductType.WholeDigestate:
      case ProductType.ThinDigestate:
        return ['Mass', 'Volume_Liquid'];
      default:
        return ['Energy', 'Enthalpy', 'Mass', 'Volume_Liquid', 'Volume_Gas'];
    }
  }

  /**
   * Returns a boolean for if the labels should say product or digestate.
   */
  showLabelForDigestate(): boolean {
    if (this.product && this.digestateTypes) {
      return this.digestateTypes.includes(this.product.type);
    }
    throw Error(this.translationService.get('PRODUCT.FINANCIAL_GEN_PROD.ERROR_PROD_TYPE'));
  }

  onSaveClick() {
    this.dialogRef.close(this.product);
  }

  onCancelClick() {
    this.dialogRef.close(null);
  }
}
